/* 
  This class is responsible for performing GPU-based grid-based computations 
  using Three.js and the GPUComputationRenderer. It sets up a GPGPU system 
  that tracks a grid, updates mouse interactions, and manages rendering and 
  computational shaders. It allows dynamic interaction with the grid via mouse 
  input and performs relaxation computations to produce fluid-like grid behavior.
*/

import * as THREE from 'three'
import { GPUComputationRenderer, Variable } from 'three/examples/jsm/misc/GPUComputationRenderer.js'
import { GPGPUShader } from './shaders'

interface Props {
  renderer: THREE.WebGLRenderer
  aspect: number
}

interface Params {
  relaxation: number
  distance: number
  strength: number
}

export interface GridSize {
  columns: number
  rows: number
}

const GRID_ROWS = 16
const REFERENCE_FRAMES_PER_SECOND = 60
const MOUSE_MOVE_DECAY_PER_REFERENCE_FRAME = 0.95
const DELTA_STRENGTH_SCALE = 100

export function getGridSize(aspect: number): GridSize {
  return {
    columns: Math.max(1, Math.round(GRID_ROWS * aspect)),
    rows: GRID_ROWS,
  }
}

export default class GPGPU {
  // Class properties
  gridSize: GridSize
  gpgpuRenderer!: GPUComputationRenderer  // GPGPU renderer instance
  renderer: THREE.WebGLRenderer           // WebGL renderer
  dataTexture!: THREE.DataTexture         // Initial data texture used in GPGPU
  variable!: Variable                     // GPGPU variable for tracking grid updates
  params: Params                          // Parameters for tuning behavior of the GPGPU system

  constructor({ renderer, aspect }: Props) {
    // Assigning properties and setting initial parameters
    this.renderer = renderer
    this.gridSize = getGridSize(aspect)
    
    this.params = {
      relaxation: 0.965,
      distance: 0.5,
      strength: 0.45,
    }

    // Initialize GPGPU system components
    this.createGPGPURenderer()
    this.createDataTexture()
    this.createVariable()
    this.setRendererDependencies()
    this.initiateRenderer()
  }

  // Initialize the GPGPU renderer with dimensions and the WebGL renderer
  createGPGPURenderer() {
    this.gpgpuRenderer = new GPUComputationRenderer(this.gridSize.columns, this.gridSize.rows, this.renderer)
  }

  // Create a data texture to hold the initial state of the grid
  createDataTexture() {
    this.dataTexture = this.gpgpuRenderer.createTexture()
  }

  // Create a GPGPU variable and assign shader and uniforms for grid interactions
  createVariable() {
    this.variable = this.gpgpuRenderer.addVariable('uGrid', GPGPUShader.fragmentShader, this.dataTexture)

    this.variable.material.uniforms.uRelaxation = new THREE.Uniform(this.params.relaxation)
    this.variable.material.uniforms.uGridSize = new THREE.Uniform(this.gridSize.rows)
    this.variable.material.uniforms.uMouse = new THREE.Uniform(new THREE.Vector2(0, 0))
    this.variable.material.uniforms.uDeltaMouse = new THREE.Uniform(new THREE.Vector2(0, 0))
    this.variable.material.uniforms.uMouseMove = new THREE.Uniform(0)
    this.variable.material.uniforms.uDistance = new THREE.Uniform(this.params.distance * 10)
  }

  // Define the dependencies between GPGPU variables for consistent updates
  setRendererDependencies() {
    this.gpgpuRenderer.setVariableDependencies(this.variable, [this.variable])
  }

  // Initialize the GPGPU renderer (compiles shaders and prepares for computation)
  initiateRenderer() {
    this.gpgpuRenderer.init()
  }

  // Update the mouse position on the grid and calculate delta for interaction effects
  updateMouse(uv: THREE.Vector2) {
    this.variable.material.uniforms.uMouseMove.value = 1 // Mark mouse as moving

    // Calculate difference between current mouse position and previous
    const lastMouse = this.variable.material.uniforms.uMouse.value as THREE.Vector2
    const deltaMouse = this.variable.material.uniforms.uDeltaMouse.value as THREE.Vector2
    deltaMouse.subVectors(uv, lastMouse).multiplyScalar(this.params.strength * DELTA_STRENGTH_SCALE)
    lastMouse.copy(uv)
  }

  // Retrieve the current texture after the GPGPU computation step
  getTexture() {
    return this.gpgpuRenderer.getCurrentRenderTarget(this.variable).textures[0]
  }

  // Perform rendering and computation steps, updating uniforms over time
  render(dt: number) {
    const elapsedReferenceFrames = dt * REFERENCE_FRAMES_PER_SECOND
    const relaxation = Math.pow(this.params.relaxation, elapsedReferenceFrames)

    this.variable.material.uniforms.uMouseMove.value *= Math.pow(MOUSE_MOVE_DECAY_PER_REFERENCE_FRAME, elapsedReferenceFrames) // Fade mouse movement over time
    this.variable.material.uniforms.uRelaxation.value = relaxation
    this.variable.material.uniforms.uDeltaMouse.value.multiplyScalar(relaxation) // Apply relaxation to mouse delta

    this.gpgpuRenderer.compute()
  }

  dispose() {
    this.variable.material.dispose()
    this.gpgpuRenderer.dispose()
  }
}